import React from "react";
import { Avatar } from "@mui/material";
import { stringAvatar } from "../../utils/AvatarUtils";

const SYSTEM_BORDER_COLOR = "#7c3aed";

/**
 * Centralised avatar for agents, users, channels — anything with a name.
 * Handles initials, color, sizing, system-agent border, and optional click.
 *
 * Props:
 *  - name        (string)  Display name (falls back to "?")
 *  - size        (number)  Width & height in px (default 28)
 *  - borderRadius (string|number) Override (default derived from size)
 *  - fontSize    (string|number) Override (default derived from size)
 *  - systemAgent (bool)    Coloured border ring for system agents
 *  - onClick     (func)    Click handler (adds pointer cursor + hover)
 *  - sx          (object)  Extra MUI sx overrides merged last
 *  - children    (node)    Override initials (e.g. custom icon or letter)
 */
export default function AgentAvatar({
  name,
  size = 28,
  borderRadius,
  fontSize,
  systemAgent = false,
  onClick,
  sx,
  children,
  ...rest
}) {
  const resolvedName = name || "?";
  const avatarProps = stringAvatar(resolvedName);

  const resolvedRadius = borderRadius ?? (size <= 24 ? `${Math.round(size * 0.2)}px` : "6px");
  const resolvedFontSize = fontSize ?? (size <= 20 ? "10px" : size <= 28 ? "12px" : size <= 40 ? "14px" : "20px");

  const clickSx = onClick
    ? {
        cursor: "pointer",
        transition: "transform 140ms ease, box-shadow 140ms ease, filter 140ms ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 2px 8px rgba(24, 24, 27, 0.2)",
          filter: "brightness(0.98)",
        },
        "&:active": {
          transform: "translateY(0)",
          boxShadow: "0 1px 4px rgba(24, 24, 27, 0.16)",
        },
      }
    : {};

  const systemSx = systemAgent
    ? {
        border: `2px solid ${SYSTEM_BORDER_COLOR}`,
      }
    : {};

  return (
    <Avatar
      variant="rounded"
      {...avatarProps}
      {...rest}
      {...(children != null ? { children } : {})}
      onClick={onClick}
      sx={{
        ...avatarProps.sx,
        width: size,
        height: size,
        borderRadius: resolvedRadius,
        fontSize: resolvedFontSize,
        fontWeight: 600,
        flexShrink: 0,
        ...clickSx,
        ...systemSx,
        ...sx,
      }}
    />
  );
}
